import React, { useMemo } from 'react';
import { Spin } from 'antd';
import styled from 'styled-components';
import type { IStaffUI } from 'types/staff';
import type { ITimeOffEntry } from '../services/types';
import { fmtTime, fmtShort, rangeDateLabel, daysBetween } from '../services/format';
import StaffAvatar from 'components/StaffAvatar';
import { IconCalendar, IconCheck, IconTrash, IconX } from './icons';

interface Props {
  entries: ITimeOffEntry[];
  staffs: IStaffUI[];
  indexById: Record<number, number>;
  avatarById: Record<number, string>;
  selectedStaffIds: number[];
  rangeStart: string;
  rangeEnd: string;
  loading: boolean;
  pendingDeleteId: number | null;
  onRequestDelete: (id: number) => void;
  onCancelDelete: () => void;
  onConfirmDelete: (id: number) => void;
}

const TimeOffTable: React.FC<Props> = ({
  entries, staffs, indexById, avatarById, selectedStaffIds, rangeStart, rangeEnd, loading,
  pendingDeleteId, onRequestDelete, onCancelDelete, onConfirmDelete,
}) => {
  const selCount = selectedStaffIds.length;

  const scopeLabel = useMemo(() => {
    if (selCount === 0) return 'All staff';
    if (selCount === 1) {
      const s = staffs.find(x => Number(x.id) === selectedStaffIds[0]);
      return s?.name || '1 staff selected';
    }
    return `${selCount} staff selected`;
  }, [selCount, selectedStaffIds, staffs]);

  const singleDay = rangeStart === rangeEnd;
  const title = singleDay
    ? `Time Off · ${fmtShort(rangeStart)}`
    : `Time Off · ${fmtShort(rangeStart)} – ${fmtShort(rangeEnd)}`;
  const count = entries.length;
  const footer = `${count} ${count === 1 ? 'entry' : 'entries'} · ${scopeLabel} · ${rangeDateLabel(rangeStart, rangeEnd)}`;

  return (
    <TableWrap>
      <Toolbar>
        <span className="title">{title}</span>
        <span className="count">{count}</span>
        <span className="scope">{scopeLabel}</span>
      </Toolbar>

      <Head>
        <span>Staff</span>
        <span>Date</span>
        <span>Time</span>
        <span>Reason</span>
        <span />
      </Head>

      <Body>
        {loading && (
          <LoadingOverlay>
            <Spin size="large" />
          </LoadingOverlay>
        )}
        {count === 0 && !loading && (
          <Empty>
            <IconCalendar size={40} strokeWidth={1.5} />
            <span>{singleDay ? 'No time off on this day' : 'No time off in this range'}</span>
          </Empty>
        )}
        {entries.map(e => {
          const confirming = pendingDeleteId === e.id;
          const days = daysBetween(e.start, e.end);
          return (
            <Row key={e.id} $confirming={confirming}>
              <Cell>
                <StaffAvatar name={e.name} avatar={avatarById[e.staffId]} gradIndex={indexById[e.staffId]} size={36} fontSize={13} />
                <span className="name">{e.name}</span>
              </Cell>
              <DateCell>
                <span className="date">{rangeDateLabel(e.start, e.end)}</span>
                {days > 1 && <span className="sub">{days} days</span>}
              </DateCell>
              <Cell>
                <TimePill $allDay={e.allDay}>
                  {e.allDay ? 'All day' : `${fmtTime(e.fromMin || 0)} – ${fmtTime(e.toMin || 0)}`}
                </TimePill>
              </Cell>
              <Cell>
                <span className="reason">{e.reason || '—'}</span>
              </Cell>
              <ActionCell>
                {confirming ? (
                  <div className="confirm">
                    <ConfirmBtn onClick={() => onConfirmDelete(e.id)} title="Confirm delete">
                      <IconCheck size={15} strokeWidth={2.6} color="#fff" />
                    </ConfirmBtn>
                    <KeepBtn onClick={onCancelDelete} title="Keep">
                      <IconX size={14} strokeWidth={2.4} />
                    </KeepBtn>
                  </div>
                ) : (
                  <TrashBtn onClick={() => onRequestDelete(e.id)} title="Delete">
                    <IconTrash size={16} />
                  </TrashBtn>
                )}
              </ActionCell>
            </Row>
          );
        })}
      </Body>

      <Footer>{footer}</Footer>
    </TableWrap>
  );
};

export default TimeOffTable;

const GRID = '1.4fr 1.5fr 1.2fr 1.4fr 44px';

const TableWrap = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background: var(--bg-app);
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px 12px;
  flex-shrink: 0;
  .title { font-size: 15px; font-weight: 700; color: var(--fg-1); }
  .count { font-size: 12px; font-weight: 700; color: #fff; background: var(--acc); border-radius: 999px; padding: 2px 9px; }
  .scope { font-size: 13px; color: var(--fg-3); margin-left: auto; }
`;

const Head = styled.div`
  display: grid;
  grid-template-columns: ${GRID};
  gap: 12px;
  padding: 10px 24px;
  background: var(--bg-subtle);
  border-top: 1px solid var(--border-1);
  border-bottom: 1px solid var(--border-1);
  flex-shrink: 0;
  span {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--fg-3);
  }
`;

const Body = styled.div`
  position: relative;
  flex: 1;
  overflow-y: auto;
`;

// Sits over the rows so stale data stays visible (dimmed) while refetching.
const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.65);
`;

const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 70px 0;
  color: var(--fg-4);
  span { font-size: 14px; font-weight: 600; }
`;

const Row = styled.div<{ $confirming: boolean }>`
  display: grid;
  grid-template-columns: ${GRID};
  gap: 12px;
  padding: 12px 24px;
  border-bottom: 1px solid var(--border-1);
  align-items: center;
  background: ${p => (p.$confirming ? 'var(--danger-100)' : 'var(--bg-surface)')};
`;

const Cell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  .name { font-size: 14px; font-weight: 600; color: var(--fg-1); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .reason { font-size: 13px; color: var(--fg-2); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
`;

const DateCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  justify-content: center;
  min-width: 0;
  .date { font-size: 14px; font-weight: 600; color: var(--fg-1); }
  .sub { font-size: 12px; color: var(--fg-3); }
`;

const TimePill = styled.span<{ $allDay: boolean }>`
  display: inline-block;
  padding: 4px 11px;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 700;
  background: ${p => (p.$allDay ? 'var(--brand-navy)' : 'var(--acc-soft)')};
  color: ${p => (p.$allDay ? '#fff' : 'var(--acc)')};
`;

const ActionCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  .confirm { display: flex; gap: 4px; }
`;

const TrashBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: var(--r-sm);
  border: none;
  background: transparent;
  color: var(--fg-4);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: var(--danger-100); color: var(--danger); }
`;

const ConfirmBtn = styled.button`
  width: 30px;
  height: 30px;
  border-radius: var(--r-sm);
  border: none;
  background: var(--danger);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const KeepBtn = styled.button`
  width: 30px;
  height: 30px;
  border-radius: var(--r-sm);
  border: 1px solid var(--border-1);
  background: var(--bg-surface);
  color: var(--fg-3);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-top: 1px solid var(--border-1);
  background: var(--bg-surface);
  flex-shrink: 0;
  font-size: 13px;
  color: var(--fg-3);
`;
